import {
  BaseQueryError,
  BaseQueryFn,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { isObject } from "common/utils/objects";
import { isUndefined } from "common/utils/identity";
import { SerializedError } from "@reduxjs/toolkit";
import { TFunction } from "i18next";

enum AUTH0_ERROR_MSGS {
  "EMAIL_UNVERIFIED" = "email_unverified",
  "ACTIVATION_REQUIRED" = "activation_required",
  "PRIMARY_IDENTITY_MISSING" = "primary_identity_missing",
  "ACCOUNT_LINKING_FAILED" = "account_linking_failed",
}

function getErrorMessageForAuth0Error(
  errorCode: string | undefined,
  t: TFunction
): string | undefined {
  switch (errorCode) {
    case AUTH0_ERROR_MSGS.EMAIL_UNVERIFIED: {
      return t("views.auth.errors.emailUnverified");
    }
    case AUTH0_ERROR_MSGS.ACTIVATION_REQUIRED: {
      return t("views.auth.errors.activationRequired");
    }
    case AUTH0_ERROR_MSGS.PRIMARY_IDENTITY_MISSING: {
      return t("views.auth.errors.primaryIdentityMissing");
    }
    case AUTH0_ERROR_MSGS.ACCOUNT_LINKING_FAILED: {
      return t("views.auth.errors.accountLinkingFailed");
    }
    default: {
      return t("views.auth.errors.accessDenied");
    }
  }
}

interface ErrorResponse {
  error?: string;
  // Custom Portal REST error `code`(not HTTP Error Code)
  code?: string;
}

// Portal REST `code` values in error JSON.
export enum CLIENT_ERROR_CODES {
  STALE_VERSION = "STALE_VERSION",
  CHIP_IDS_MISSING = "CHIP_IDS_MISSING",
}

type RtkErrorWithData = { data: ErrorResponse };
function isRtkErrorWithData(error: unknown): error is RtkErrorWithData {
  return isObject(error) && isObject(error.data);
}

// Portal REST `code` field from error response JSON.
export const getErrorCode = (error: unknown): string | undefined => {
  if (!isRtkErrorWithData(error)) {
    return undefined;
  }
  const code = error.data.code;
  return typeof code === "string" ? code : undefined;
};

const unwrapMutationError = (error: unknown): unknown => {
  if (
    error &&
    typeof error === "object" &&
    "error" in error &&
    (error as { error?: unknown }).error !== undefined
  ) {
    return (error as { error: unknown }).error;
  }
  return error;
};

export const isStalePlantProfileSaveError = (error: unknown): boolean =>
  getErrorCode(unwrapMutationError(error)) === CLIENT_ERROR_CODES.STALE_VERSION;

export const resolvePopupErrorOverride = (
  error: unknown,
  portalErrorMessagesByCode: Partial<Record<string, string>> | undefined
): string | undefined => {
  const code = getErrorCode(error);
  if (!code) {
    return;
  }
  return portalErrorMessagesByCode?.[code];
};

export const formatError = (
  t: TFunction,
  error: FetchBaseQueryError | SerializedError | string | undefined
): string | undefined => {
  if (isUndefined(error)) {
    return;
  }
  if (typeof error === "string") {
    return error;
  }
  if (
    "message" in error &&
    "error" in error &&
    error.error === "access_denied"
  ) {
    return getErrorMessageForAuth0Error(error.message, t);
  }
  if ("message" in error) {
    return error.message;
  }
  if ("data" in error && isObject(error.data) && "error" in error.data) {
    return error.data.error;
  }
  if ("error" in error) {
    return error.error;
  }
  if ("status" in error) {
    return String(error.status);
  }
  return t("components.ErrorBoundary.error");
};

export const handleError = <BaseQuery extends BaseQueryFn>(
  t: TFunction,
  isError: boolean,
  error:
    | FetchBaseQueryError
    | SerializedError
    | BaseQueryError<BaseQuery>
    | undefined,
  callback: (message?: string) => void
): void => {
  if (!isError) {
    return;
  }
  const fallback = t("components.ErrorBoundary.error");
  if (
    !error ||
    typeof error !== "object" ||
    !("data" in error) ||
    error.data === null
  ) {
    callback(fallback);
    return;
  }
  callback((error.data as ErrorResponse).error ?? fallback);
};

// accommodating for RTK returning a plain object for abort errors
export const isAbort = (error: unknown): boolean =>
  Boolean(
    error &&
      typeof error === "object" &&
      (error as { name?: string }).name === "AbortError"
  );

// Use `myPromise.catch(swallowAbort)` to turn an `AbortError` into a silent
// success, while letting any other errors still throw.
export const swallowAbort = (reason: unknown): Promise<void> =>
  isAbort(reason) ? Promise.resolve() : Promise.reject(reason);
